<template>
  <div class="h-screen overflow-hidden bg-theme-paper">
    <div class="h-full overflow-hidden">
      <div class="flex h-full bg-theme-paper">
        <div
          class="flex flex-col w-full h-full overflow-hidden transition-[padding] duration-slow ease-sidebar lg:h-full"
          :class="HR ? (isCollapsed ? 'lg:pl-sidebar-nav-collapsed' : 'lg:pl-sidebar-nav') : ''"
        >
          <nav
            class="fixed top-0 z-navbar left-0 w-full px-4 h-12 lg:h-[85px] flex mx-auto shadow-card bg-theme-paper transition-[padding] duration-slow ease-sidebar"
            :class="
              HR ? (isCollapsed ? 'lg:pl-[8.5rem]' : 'lg:pl-sidebar-nav') : 'lg:pl-14 lg:pr-10'
            "
          >
            <div class="flex w-full">
              <div class="flex justify-end w-full py-px lg:flex-row lg:py-4">
                <div
                  class="flex w-full gap-2"
                  :class="HR ? '' : 'lg:grid lg:grid-cols-[1fr_auto_1fr]'"
                >
                  <div
                    class="flex items-center justify-center order-2 w-auto lg:order-1 lg:justify-start shrink-0"
                    v-if="!HR || (HR && isSmallerScreen)"
                    data-testid="top-nav-logo"
                  >
                    <ApplicationLogo :tile="isSmallerScreen" :with-wordmark="!isSmallerScreen" />
                  </div>

                  <NavMenu
                    class="flex items-center justify-center order-1 lg:order-2 lg:justify-start"
                    :nav-component="nav"
                    @menuStateChange="handleMenuDropdownOpen"
                    @collapsingSidebar="collapsingSidebar"
                    @toggle="toggleMenu"
                  />
                  <div class="flex items-center justify-end order-3 ml-auto shrink-0">
                    <div class="flex items-center lg:gap-2" :class="!auth.user ? 'gap-2' : 'gap-2'">
                      <TopIconMenu />
                      <div class="relative flex items-center justify-center" v-if="auth.user">
                        <Dropdown
                          align="right"
                          width="44"
                          overlayClasses="top-[42px] lg:top-[68px] !mt-0 right-[-11px]"
                          contentClasses="py-0"
                        >
                          <template #trigger>
                            <div class="flex items-center justify-center cursor-pointer">
                              <Avatar
                                v-if="auth.user"
                                :size="!isSmallerScreen ? '10' : '8'"
                              />
                            </div>
                          </template>

                          <template #content>
                            <div
                              @click.stop="$refs.themeSwitcher.toggleDarkMode()"
                              class="flex items-center px-4 py-2 transition ease-in-out cursor-pointer duration-fast h-11 hover:bg-grey-100 dark:hover:bg-grey-700 focus:bg-grey-100 dark:focus:bg-grey-700"
                            >
                              <div class="flex items-center justify-between w-full">
                                <span class="text-sm font-bold dark:text-theme-secondary text-theme-primary">
                                  {{ $t('Theme') }}
                                </span>
                                <ThemeSwitcher ref="themeSwitcher" />
                              </div>
                            </div>

                            <div
                              @click.stop="$refs.languageSwitcher.toggle($event)"
                              class="flex items-center px-4 py-2 transition ease-in-out cursor-pointer duration-fast h-11 hover:bg-grey-100 dark:hover:bg-grey-700 focus:bg-grey-100 dark:focus:bg-grey-700"
                            >
                              <div class="flex items-center justify-between w-full">
                                <span class="text-sm font-bold dark:text-theme-secondary text-theme-primary">
                                  {{ $t('Language') }}
                                </span>
                                <div @click.stop>
                                  <LanguageSwitcher ref="languageSwitcher" />
                                </div>
                              </div>
                            </div>

                            <DropdownLink
                              :href="route('settings.index')"
                              class="flex items-center justify-between h-11"
                            >
                              <span class="text-sm font-bold dark:text-theme-secondary text-theme-primary">
                                {{ $t('Settings') }}
                              </span>
                              <div class="flex items-center justify-center w-5 h-5">
                                <Icon width="20" height="20" type="settings" />
                              </div>
                            </DropdownLink>

                            <DropdownLink
                              v-if="nav === 'candidate'"
                              :href="route('profile')"
                              class="flex items-center justify-between h-11"
                            >
                              <span class="text-sm font-bold dark:text-theme-secondary text-theme-primary">
                                {{ $t('Profile') }}
                              </span>
                              <div class="flex items-center justify-center w-5 h-5">
                                <Icon type="profile" width="10" />
                              </div>
                            </DropdownLink>
                            <DropdownLink
                              @click="logout"
                              :href="'#'"
                              as="button"
                              class="flex items-center text-sm font-bold dark:text-theme-secondary text-theme-primary h-11"
                            >
                              {{ $t('Log Out') }}
                            </DropdownLink>
                          </template>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </nav>
          <!--
            'Speak to an agent' widget disabled — the New chat action in the navigation
            now covers AI access from any page (Product Review, 16 Jul 2026).
            <div>
              <ElevenLabsWidget
                v-if="hasPermissions(USER_PERMISSIONS.HR_PORTAL) && nav !== 'admin2' && !($page.component === 'HRAssistant/Index' && isSmallerScreen)"
                :agentId="'4vweir7ZSEqxr5Th2Iyl'"
              />
            </div>
          -->
          <div scroll-region class="flex flex-col h-full pt-16 overflow-auto lg:pt-28">
            <div
              class="flex flex-col justify-start flex-auto w-full px-px pb-4 mx-auto lg:overflow-visible lg:pb-6"
              :class="contentWidthClass"
            >
              <EmailVerifyBanner v-if="$page.props.emailUnverified" />
              <slot />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ovn-toast ref="toast"></ovn-toast>
</template>

<script>
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import Avatar from '@/Components/Avatar.vue';
import Dropdown from '@/Components/Dropdown.vue';
import NavHR from '@/Layouts/Navigation/NavHR.vue';
import DropdownLink from '@/Components/DropdownLink.vue';
import Icon from '@/Icons/OptimizedIcons.vue';
import OvnToast from '@/Components/Toast.vue';
import EmailVerifyBanner from '@/Components/EmailVerifyBanner.vue';
import NavMenu from '@/Components/NavMenu.vue';
import ThemeSwitcher from '@/Components/ThemeSwitcher.vue';
import TopIconMenu from '@/Layouts/Partials/TopIconMenu.vue';
import LanguageSwitcher from '@/Layouts/Partials/LanguageSwitcher.vue';
// import ElevenLabsWidget from '@/Components/ElevenLabsWidget.vue';
import { hasPermissions } from '@/Utils/permissions.js';
import { escapeHtml } from '@/Utils/stringUtils.js';
import { USER_PERMISSIONS } from '@/constants.js';
import { ensureEcho } from '@/echo.js';
import axios from 'axios';
export default {
  name: 'AuthenticatedLayout',
  components: {
    Avatar,
    Dropdown,
    DropdownLink,
	 ApplicationLogo,
    Icon,
    OvnToast,
    EmailVerifyBanner,
    NavMenu,
    ThemeSwitcher,
    TopIconMenu,
    LanguageSwitcher,
    // ElevenLabsWidget,
    NavHR,
	 ApplicationLogo,
  },
  data() {
    return {
      auth: this.$page.props.auth,
      fcmStore: null,
		 isSmallerScreen: false,
      USER_PERMISSIONS,
      isCollapsed: false,
    };
  },
  props: {
    nav: String,
    fullWidth: Boolean,
    narrow: Boolean,
  },
  provide() {
    return {
      showToastGlobal: this.showToast,
    };
  },
  computed: {
    HR() {
      return this.nav === 'hr' || this.nav === 'admin2';
    },
    contentWidthClass() {
      if (this.fullWidth) {
        return 'max-w-none';
      }

      return this.narrow ? 'max-w-content-narrow' : 'max-w-content';
    },
  },
  methods: {
	checkScreenSize() {
     this.isSmallerScreen = window.innerWidth < 1024;
    },
    hasPermissions,
    collapsingSidebar(isCollapsed) {
      this.isCollapsed = isCollapsed;
    },
    async initFcm() {
      if (this.auth.user) {
        try {
          const fcmModule = await import('@/Stores/useFcmStore.js');
          this.fcmStore = fcmModule.useFcmStore();

          await this.fcmStore.firebaseInit((payload) => {
            this.onPushMessageReceived(payload);
          });
        } catch (error) {
          console.error('Error loading FCM module:', error);
        }
      }
    },
    toggleMenu() {
      // Toggle is only shown on mobile, no action needed
    },
    handleMenuDropdownOpen(isOpen) {
      // Menu state change handled by NavMenu
    },
    updateUserStatus() {
      if (this.auth.user) {
        axios.put(route('chat.user.update.status'));
      }
    },
    onPushMessageReceived(payload) {
      let body = `<div><strong>${escapeHtml(payload.notification.title)}</strong>`;
      body += `<p>${escapeHtml(payload.notification.body).replace(/\r?\n/g, '<br>')}</p></div>`;
      let message = '';
      if (/^https?:\/\//i.test(payload.fcmOptions?.link ?? '')) {
        message = `<a href="${escapeHtml(payload.fcmOptions.link)}" class="w-full">`;
        message += body;
        message += `</a>`;
      } else {
        message = body;
      }
      this.$refs.toast?.show(message, 'info', 7000);
    },
    logout() {
      this.$inertia.post(route('logout'));
      this.auth.user = null;
    },
    showToast(message, type = 'success') {
      this.$refs.toast?.show(message, type);
    },
    showFlashMessage() {
      if (this.$page.props.toastMessage?.message) {
        let type = this.$page.props.toastMessage.type || 'info';
        this.$refs.toast?.show(this.$page.props.toastMessage.message, type);
        this.$page.props.toastMessage = null;
      }
    },
    async initEcho() {
      await ensureEcho();
    },
  },
  mounted() {
    this.showFlashMessage();
    this.checkScreenSize();
	 window.addEventListener('resize', this.checkScreenSize);
    if (this.auth.user) {
      this.updateUserStatus();
      this.initEcho();
      this.initFcm();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  updated() {
    this.updateUserStatus();
    this.showFlashMessage();
  },
};
</script>

<style scoped></style>
